/**
 * Admin Marketing Analytics — funnel + source/campaign rollups from
 * `ad_landings` + `ad_conversions`. Read-only.
 */
import { useEffect, useState } from "react";
import { RefreshCw, TrendingUp, Radio, MousePointerClick, Download, Store, Filter } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import UtmBuilder from "@/components/UtmBuilder";

const WINDOWS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

const SOURCE_LABELS = {
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  tiktok_ads: "TikTok Ads",
  paid_ads: "Paid Ads (other)",
  unknown: "Unknown",
};

export default function AdminMarketingAnalytics() {
  const [window_, setWindow] = useState("30d");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ window: window_ });
      if (source) qs.set("source", source);
      const r = await api.get(`/admin/marketing/analytics?${qs}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window_, source]);

  const t = data?.totals;
  const maxDaily = Math.max(1, ...(data?.daily || []).map((d) => d.landings));

  return (
    <div className="p-6 space-y-6" data-testid="admin-marketing-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><Radio className="w-3 h-3" /> Marketing</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Ad Campaign Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Landings → PWA installs → first offer activations, rolled up by source and campaign.</p>
        </div>
        <button onClick={load} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="marketing-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="marketing-filters">
        <div className="flex items-center gap-1 rounded-full bg-white/80 backdrop-blur border border-slate-200 p-1 text-xs font-semibold">
          {WINDOWS.map((w) => (
            <button key={w.key} onClick={() => setWindow(w.key)}
                    className={`px-3 py-1 rounded-full transition ${window_ === w.key ? "bg-cyan-500 text-white" : "text-slate-500 hover:text-slate-900"}`}
                    data-testid={`marketing-window-${w.key}`}>
              {w.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3 h-3 text-slate-400" />
          <select value={source} onChange={(e) => setSource(e.target.value)}
                  className="input text-xs" data-testid="marketing-source-filter">
            <option value="">All sources</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
            <option value="tiktok_ads">TikTok Ads</option>
            <option value="paid_ads">Paid Ads (other)</option>
          </select>
        </div>
      </div>

      {loading && !data && (
        <div className="p-12 text-center text-sm text-slate-500" data-testid="marketing-loading">Loading analytics…</div>
      )}

      <UtmBuilder />

      {t && (
        <>
          {/* Funnel */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="marketing-funnel">
            <FunnelCard label="Landings" value={t.landings} icon={MousePointerClick} tone="cyan" testid="funnel-landings" />
            <FunnelCard label="PWA Installs" value={t.installs} icon={Download} tone="emerald" testid="funnel-installs" pct={t.install_rate} of="landings" />
            <FunnelCard label="First Activations" value={t.activations} icon={TrendingUp} tone="violet" testid="funnel-activations" pct={t.activation_rate} of="landings" />
            <FunnelCard label="Install rate" value={`${t.install_rate}%`} icon={TrendingUp} tone="slate" testid="funnel-installrate" />
            <FunnelCard label="Activation rate" value={`${t.activation_rate}%`} icon={TrendingUp} tone="slate" testid="funnel-activationrate" />
          </div>

          {/* Time series */}
          {data.daily.length > 0 && (
            <div className="pf-card p-5" data-testid="marketing-timeseries">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-slate-900 text-lg">Daily volume</h2>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-cyan-500" /> Landings</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" /> Installs</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-violet-500" /> Activations</span>
                </div>
              </div>
              <div className="flex items-end gap-1 h-32 overflow-x-auto">
                {data.daily.map((d) => (
                  <div key={d.day} className="flex flex-col items-center gap-0.5 min-w-[36px]" title={d.day}>
                    <div className="flex items-end gap-0.5 h-24">
                      <div className="w-2 rounded-t bg-cyan-500" style={{ height: `${(d.landings / maxDaily) * 96}px` }} />
                      <div className="w-2 rounded-t bg-emerald-500" style={{ height: `${(d.installs / maxDaily) * 96}px` }} />
                      <div className="w-2 rounded-t bg-violet-500" style={{ height: `${(d.activations / maxDaily) * 96}px` }} />
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono">{d.day.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By source */}
          <div className="pf-card overflow-hidden" data-testid="marketing-by-source">
            <div className="p-4 border-b border-white/60">
              <h2 className="font-display text-slate-900 text-lg">By source</h2>
              <p className="text-xs text-slate-500 mt-0.5">Which ad platforms drive the most activations.</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-2">Source</th>
                  <th className="px-5 py-2 text-right">Landings</th>
                  <th className="px-5 py-2 text-right">Installs</th>
                  <th className="px-5 py-2 text-right">Activations</th>
                  <th className="px-5 py-2 text-right">Install %</th>
                  <th className="px-5 py-2 text-right">Activation %</th>
                </tr>
              </thead>
              <tbody>
                {data.by_source.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500 text-sm">No ad landings yet.</td></tr>
                )}
                {data.by_source.map((r) => (
                  <tr key={r.source} className="border-t border-white/60" data-testid={`by-source-${r.source}`}>
                    <td className="px-5 py-2 font-semibold text-slate-900">{SOURCE_LABELS[r.source] || r.source}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.landings}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.installs}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.activations}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.install_rate}%</td>
                    <td className="px-5 py-2 text-right font-mono">{r.activation_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* By campaign */}
          <div className="pf-card overflow-hidden" data-testid="marketing-by-campaign">
            <div className="p-4 border-b border-white/60 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-slate-900 text-lg">Top campaigns</h2>
                <p className="text-xs text-slate-500 mt-0.5">Ranked by activations. Empty utm_campaign shows as "(no utm_campaign)".</p>
              </div>
              <button onClick={async () => {
                        try {
                          const qs = new URLSearchParams({ window: window_ });
                          if (source) qs.set("source", source);
                          const r = await api.get(`/admin/marketing/analytics.csv?${qs}`,
                                                    { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([r.data]));
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `cyannect-ad-campaigns-${window_}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Campaigns CSV downloaded.");
                        } catch { toast.error("CSV export failed"); }
                      }}
                      className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 shrink-0"
                      data-testid="marketing-export-csv">
                <Download className="w-3 h-3" /> Export CSV
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-2">Source</th>
                  <th className="px-5 py-2">Campaign</th>
                  <th className="px-5 py-2 text-right">Landings</th>
                  <th className="px-5 py-2 text-right">Installs</th>
                  <th className="px-5 py-2 text-right">Activations</th>
                  <th className="px-5 py-2 text-right">Activation %</th>
                </tr>
              </thead>
              <tbody>
                {data.by_campaign.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500 text-sm">No campaigns tagged yet.</td></tr>
                )}
                {data.by_campaign.slice(0, 20).map((r, i) => (
                  <tr key={`${r.source}:${r.campaign}:${i}`} className="border-t border-white/60"
                      data-testid={`by-campaign-${i}`}>
                    <td className="px-5 py-2 text-xs">{SOURCE_LABELS[r.source] || r.source}</td>
                    <td className="px-5 py-2 font-semibold text-slate-900">{r.campaign}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.landings}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.installs}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.activations}</td>
                    <td className="px-5 py-2 text-right font-mono">{r.activation_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* By store */}
          {data.by_store.length > 0 && (
            <div className="pf-card overflow-hidden" data-testid="marketing-by-store">
              <div className="p-4 border-b border-white/60">
                <h2 className="font-display text-slate-900 text-lg inline-flex items-center gap-2">
                  <Store className="w-4 h-4 text-cyan-600" /> Top deep-link storefronts
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Which brands ads route shoppers to via <code className="text-[10px] px-1 py-0.5 rounded bg-slate-100">?store=slug</code>.</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-white/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-5 py-2">Storefront slug</th>
                    <th className="px-5 py-2 text-right">Installs</th>
                    <th className="px-5 py-2 text-right">Activations</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_store.map((r) => (
                    <tr key={r.store} className="border-t border-white/60" data-testid={`by-store-${r.store}`}>
                      <td className="px-5 py-2 font-mono text-xs">{r.store}</td>
                      <td className="px-5 py-2 text-right font-mono">{r.installs}</td>
                      <td className="px-5 py-2 text-right font-mono">{r.activations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FunnelCard({ label, value, icon: Icon, tone, testid, pct, of }) {
  const tones = {
    cyan: "from-cyan-500 to-cyan-600",
    emerald: "from-emerald-500 to-emerald-600",
    violet: "from-violet-500 to-violet-600",
    slate: "from-slate-500 to-slate-600",
  };
  return (
    <div className="pf-card p-4" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tones[tone]} text-white flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-slate-900 mt-0.5">{value}</div>
      {pct !== undefined && (
        <div className="text-[10px] text-slate-500 mt-0.5">{pct}% of {of}</div>
      )}
    </div>
  );
}
